import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'

import type { components, operations } from '../constants/schema'

import { HttpReq, updateToken } from '../utils'

import RootStore from './root'
import { SingleSubStore } from './subStore'
import { abrSetUser, abrUserSignout, abrUserSignup } from '../utils/abr'
import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { format } from 'libphonenumber-js'

export class AuthStore {
  rootStore: RootStore
  userInfo: components['schemas']['UserDto'] | null = null
  isLoading = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  resetUserInfo = () => {
    localStorage.removeItem('userId')
    runInAction(() => {
      this.userInfo = null
    })
  }

  checkLogin = async (): Promise<boolean> => {
    try {
      const response = await HttpReq<boolean>({
        method: 'GET',
        url: '/user/check',
      })
      if (!response) this.resetUserInfo()
      return response
    } catch (error) {
      console.log('error: ', { error })
      this.resetUserInfo()
      return false
    } finally {
      updateToken('save')
    }
  }

  localLogin = async ({ id = 1 }: { id: number | undefined }) => {
    try {
      await HttpReq({
        method: 'GET',
        url: `/auth/login/${id}`,
      })
      await this.getUserInfo()
      if (this.userInfo?.status === 'NEED_REQUIRE_DATA_INPUT') {
        window.location.href = '/signUp'
        return
      }
      window.history.back()
    } catch (error) {
      console.log('error: ', { error })
    }
  }

  /**
   * OAuth 로그인 기능(1/2)
   */
  oAuthSignIn = async (data: operations['AuthController_webUrl']['parameters']['query']) => {
    try {
      const { url } = await HttpReq<components['schemas']['ResAuthWebUrl']>({
        method: 'GET',
        url: `/auth/web/url`,
        data,
      })
      window.location.href = url
    } catch (err: unknown) {
      console.log(err as AxiosError)
    }
  }

  /**
   * OAuth 로그인 기능(1/2)
   */
  oAuthVerify = async (data: operations['AuthController_webVerify']['parameters']['query']) => {
    return await HttpReq<components['schemas']['ResAuthWebVerificationInfo']>({
      method: 'GET',
      url: `/auth/web/verify`,
      data,
    })
  }

  getUserInfo = async (): Promise<components['schemas']['UnmaskedUserDto'] | null> => {
    runInAction(() => {
      // this.userInfo = null
      this.isLoading = true
    })
    try {
      const resData = await HttpReq<components['schemas']['UnmaskedUserDto']>({
        method: 'GET',
        url: `/user/current/unmask`,
      })

      localStorage.setItem('userId', String(resData.id))
      runInAction(() => {
        this.userInfo = resData
      })

      //Airbridge 유저 업데이트 @ahn
      abrSetUser(resData)

      //알림톡 유저 프로필 업데이트 @ahn
      const atProfile: Record<string, any> = {}
      if (resData?.name) atProfile.name = resData.name
      if (resData?.email) atProfile.email = resData.email
      if (resData?.identification?.danalName) atProfile.name = resData.identification.danalName
      if (resData?.identification?.danalPhoneNumber) {
        if (resData && resData?.identification?.danalPhoneNumber) {
          atProfile.mobileNumber = format(resData.identification.danalPhoneNumber, 'KR', 'E.164')
        }
      }

      ChannelService.updateUser({ language: 'ko', profile: atProfile })

      return resData
    } catch (err: unknown) {
      return null
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /* 로그아웃  */
  signOut = async () => {
    try {
      await HttpReq<unknown>({
        method: 'GET',
        url: `/auth/logout`,
      }).then(() => {
        abrUserSignout()
        ChannelService.track('signout')
        ChannelService.shutdown()
      })
      this.resetUserInfo()
      updateToken('remove')
    } catch (error) {
      console.log({ error })
    }
  }

  /* 회원가입  */
  signUp = async (data: components['schemas']['ReqUserAddUserTerms']) => {
    const resData = await HttpReq<components['schemas']['UserDto']>({
      method: 'POST',
      url: `/auth/signup`,
      data,
    })

    //Airbridge 유저 업데이트
    abrSetUser(resData)

    //알림톡 유저 프로필 업데이트
    const atProfile: Record<string, any> = {}
    if (resData?.name) atProfile.name = resData.name
    if (resData?.email) atProfile.email = resData.email
    if (resData?.identification?.danalPhoneNumber)
      atProfile.mobileNumber = format(resData.identification.danalPhoneNumber, 'KR', 'E.164')
    ChannelService.updateUser({ language: 'ko', profile: atProfile })

    abrUserSignup({ provider: resData?.auth?.provider })
    ChannelService.track('signup')

    return resData
  }

  /* 회원 탈퇴 */
  assetSummaryWithdrawStore = new SingleSubStore<components['schemas']['ResUserAssetSummaryForWithdraw']>(
    `/user/current/assetSummary/withdraw`,
  )
}
